import * as React from 'react';
import * as dataAtIds from '../dataAtIds.json';
import { TypeIconName } from '../Icon/tsx';
import AddonBeamLink from './AddonBeamLink';
import Flex, { Props as FlexProps } from '../Flex/Flex';
import Hamburger from '../Hamburger/Hamburger';
import Badge from '../Badge/Badge';
import Icon from '../Icon/Icon';
import Link from '../Link/Link';
import Image from '../Image/Image';
import Text from '../Text/Text';
import './AddonBeam.css';

export interface Props extends FlexProps {
  /** The icon that appears to the very left of the beam that represents the add-on */
  addonIcon: TypeIconName;
  /** The name that appears next to the add-on icon */
  addonName: string;
  /** Image src for the Bitrise application */
  appImage?: string;
  /** Link back to the Bitrise application */
  appLink?: string;
  /** Name for the Bitrise application */
  appName?: string;
  /** Flag that indicates with a badge that the addon is in a Beta stage  */
  isBeta?: boolean;
  /** Flag for if the Hamburger menu icon should be in an active state */
  isHamburgerIconActive?: boolean;
  /** */
  isInResponsiveView?: boolean;
  /** Event handler for when the Hamburger menu item is clicked */
  onHamburgerIconClick?: (event: React.SyntheticEvent) => void;
}

/** The common internal Add-on Beam for Bitrise made add-ons */
const AddonBeam: React.FunctionComponent<Props> = (props: Props) => {
  const {
    addonIcon,
    addonName,
    appImage,
    appLink,
    appName,
    children,
    isBeta,
    isHamburgerIconActive,
    isInResponsiveView,
    onHamburgerIconClick,
    ...rest
  } = props;

  return (
    <Flex { ...rest }
        alignChildrenHorizontal="between"
        alignChildrenVertical="middle"
        className="AddonBeam"
        container
        direction="horizontal">
      { isInResponsiveView && !!onHamburgerIconClick && (
        <Flex>
          <Link
              color="white"
              onClick={ onHamburgerIconClick }>
            <Hamburger active={ isHamburgerIconActive } data-bitkit-at-id={ dataAtIds.AddonBeamHamburger } />
          </Link>
        </Flex>
      ) }

      <Flex
          absolute={ isInResponsiveView ? 'center' : undefined }
          alignChildrenVertical="middle"
          direction="horizontal"
          gap="x2">
        <Flex>
          <Icon name={ addonIcon } />
        </Flex>

        <Flex>
          <Text size="x3">
            <Text inline weight="bold">{ addonName }</Text> by Bitrise
          </Text>
        </Flex>

        { isBeta && (
          <Flex>
            <Badge
                backgroundColor="yellow-3"
                textColor="grape-5">
              BETA
            </Badge>
          </Flex>
        ) }
      </Flex>

      { !isInResponsiveView && appName && (
        <Flex absolute="center">
          <AddonBeamLink href={ appLink }>
            <Flex
                alignChildrenVertical="middle"
                direction="horizontal"
                gap="x2">
              { appImage && (
                <Flex>
                  <Image
                      borderRadius="x1"
                      height="1.5rem"
                      src={ appImage }
                      width="1.5rem" />
                </Flex>
              ) }

              <Flex
                  alignChildrenVertical="middle"
                  direction="horizontal"
                  gap="x1">
                <Flex>{ appName }</Flex>
                <Flex>
                  <Icon name="OpenInBrowser" />
                </Flex>
              </Flex>
            </Flex>
          </AddonBeamLink>
        </Flex>
      ) }


      { children && !isInResponsiveView && (
        <Flex
            alignChildrenVertical="middle"
            direction="horizontal"
            gap="x6">
          { children }
        </Flex>
      ) }
    </Flex>
  );
};

export default AddonBeam;
